/**
 * Semantic version comparator.
 *
 * Pure, React-independent helper providing a total ordering over `x.y.z`
 * semantic version strings. Used for newest-first sorting (Req 4.2),
 * Latest_Version selection, and new-marking (Req 6.1). Never performs I/O and
 * never throws: malformed or missing fields are treated as `0`, and any
 * pre-release/build suffix beyond the patch is ignored.
 */

/** Parse a single `x.y.z` field to an integer, treating NaN/missing as 0. */
function parseField(value: string | undefined): number {
  const n = Number.parseInt(value ?? '', 10);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Compare two `x.y.z` semantic version strings.
 *
 * Compares major, then minor, then patch numerically (not lexicographically).
 * Returns a negative number if `a < b`, `0` if equal, and a positive number if
 * `a > b` — only the sign is meaningful. Any pre-release/build suffix
 * (introduced by `-` or `+`) is stripped before comparison, missing fields are
 * treated as `0`, and non-numeric fields are treated as `0`. Total: never throws.
 */
export function compareSemver(a: string, b: string): number {
  // Drop any pre-release/build metadata suffix, then split into x.y.z parts.
  const partsA = a.split(/[-+]/)[0].split('.');
  const partsB = b.split(/[-+]/)[0].split('.');

  for (let i = 0; i < 3; i += 1) {
    const diff = parseField(partsA[i]) - parseField(partsB[i]);
    if (diff !== 0) {
      return diff;
    }
  }

  return 0;
}
